import { Card } from "@/components/flexkit/Card";
import { ChartContainer, ChartTooltip } from "@/components/Chart";
import type { PlainWorkflow, PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import {
    WorkflowExecutionAction_Status_StepStatus_StepPhase,
    WorkflowExecutionActionPhase,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import { cn } from "@/components/podkit/lib/cn";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import * as RechartsPrimitive from "recharts";
import { Text } from "@/components/podkit/typography/Text";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { useListProjects } from "@/queries/project-queries";
import { getProjectIDFromContext } from "@/utils/automations";
import { hasWorkflowExecutionActionFailed } from "@/utils/automation-execution-action";
import { CHART_COLORS, CHART_DIMENSIONS } from "@/components/automations/workflow-chart-constants";

type Props = {
    workflow?: PlainWorkflow;
    actions: PlainWorkflowExecutionAction[];
    className?: string;
};

/**
 * Aggregated stacked area chart showing completed vs failed distribution across steps
 * for all actions within a workflow execution.
 */
export const WorkflowExecutionPerformanceChart: FC<Props> = ({ workflow, actions, className }) => {
    const stepsCount = workflow?.spec?.action?.steps?.length ?? 0;
    const { data: projectsData } = useListProjects({ enabled: true });
    const [hoveredStepIndex, setHoveredStepIndex] = useState<number | null>(null);

    // Keyboard navigation support
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (stepsCount === 0) return;

            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    setHoveredStepIndex((prev) => {
                        if (prev === null) return 0;
                        return Math.max(0, prev - 1);
                    });
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    setHoveredStepIndex((prev) => {
                        if (prev === null) return 0;
                        return Math.min(stepsCount - 1, prev + 1);
                    });
                    break;
                case "Escape":
                    event.preventDefault();
                    setHoveredStepIndex(null);
                    break;
            }
        },
        [stepsCount],
    );

    // Mouse move handler for the chart container to capture all hover events
    const handleChartMouseMove = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (stepsCount === 0) return;

            const chartElement = event.currentTarget.querySelector('.recharts-surface');
            if (!chartElement) return;

            const rect = chartElement.getBoundingClientRect();
            const mouseX = event.clientX - rect.left;

            // Get the chart dimensions (accounting for margins)
            const chartWidth = rect.width;
            const leftMargin = 48; // From AreaChart margin
            const rightMargin = 8;
            const plotWidth = chartWidth - leftMargin - rightMargin;

            // Calculate which step based on mouse position
            const relativeX = mouseX - leftMargin;
            if (relativeX >= 0 && relativeX <= plotWidth) {
                const stepIndex = Math.floor((relativeX / plotWidth) * stepsCount);
                if (stepIndex >= 0 && stepIndex < stepsCount) {
                    setHoveredStepIndex((prev) => (prev === stepIndex ? prev : stepIndex));
                }
            }
        },
        [stepsCount],
    );

    const handleChartMouseLeave = useCallback(() => {
        setHoveredStepIndex(null);
    }, []);

    const projectNameById = useMemo(
        () => Object.fromEntries((projectsData?.projects || []).map((p) => [p.id, p.metadata?.name || "-"])),
        [projectsData?.projects],
    );

    // Helper function to determine the state of a specific step instance for an action
    const determineStepState = useCallback((action: PlainWorkflowExecutionAction, stepIndex: number): 'completed' | 'running' | 'pending' | 'failed' => {
        const stepStatuses = action.status?.stepStatuses ?? [];
        const statusForStep = stepStatuses.find((s) => {
            if (!s) return false;
            return (s.stepIndex ?? 0) === stepIndex;
        });
        const actionFailed = hasWorkflowExecutionActionFailed(action);

        // Check explicit status for this step first
        if (statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED) {
            return 'failed';
        }
        if (statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE) {
            return 'completed';
        }
        if (statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.RUNNING) {
            return 'running';
        }
        if (statusForStep?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.PENDING) {
            return 'pending';
        }

        // No explicit status for this step - infer from other information
        // Note: We don't check for earlier failures here because those actions
        // are filtered out before calling this function

        // Check if action completed a later step (means this step must have completed)
        const completedLater = stepStatuses.some((s) => {
            if (!s) return false;
            return (
                (s.stepIndex ?? -1) > stepIndex &&
                s.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE
            );
        });
        if (completedLater) {
            return 'completed';
        }

        // Check if action is done overall (all steps completed)
        if (action.status?.phase === WorkflowExecutionActionPhase.DONE && !actionFailed) {
            return 'completed';
        }

        // Check if action failed overall without specific step info
        // Find the last step it reached and mark that as failed
        if (actionFailed) {
            const lastStepIndex = stepStatuses.length > 0 
                ? Math.max(...stepStatuses.map(s => s?.stepIndex ?? 0))
                : 0;
            
            return stepIndex === lastStepIndex ? 'failed' : 'pending';
        }

        // Default: step hasn't been reached yet
        return 'pending';
    }, []);

    // Build raw per-step stats
    // For each step position, count only actions that haven't failed at earlier steps
    const perStep = useMemo(
        () =>
            Array.from({ length: stepsCount }).map((_, index) => {
                let completed = 0;
                let running = 0;
                let pending = 0;
                let failed = 0;
                let totalAtThisStep = 0; // Count of actions that reached this step
                let excluded = 0; // Count of actions that failed at earlier steps

                const failedProjectNames: string[] = [];
                
                for (const action of actions) {
                    const stepStatuses = action.status?.stepStatuses ?? [];
                    const actionFailed = hasWorkflowExecutionActionFailed(action);
                    
                    // Check if this action failed at an earlier step
                    const failedEarlier = stepStatuses.some((s) => {
                        if (!s) return false;
                        return (
                            (s.stepIndex ?? 0) < index &&
                            s.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED
                        );
                    });
                    
                    // If action failed at an earlier step, count it as excluded
                    if (failedEarlier) {
                        excluded++;
                        continue;
                    }
                    
                    // Special case: action failed overall but has no FAILED step status
                    // Find the last step it reached and assume it failed there
                    if (actionFailed && !stepStatuses.some(s => s?.phase === WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED)) {
                        const lastStepIndex = stepStatuses.length > 0 
                            ? Math.max(...stepStatuses.map(s => s?.stepIndex ?? 0))
                            : 0;
                        
                        // If we're past the step where it failed, count as excluded
                        if (index > lastStepIndex) {
                            excluded++;
                            continue;
                        }
                    }
                    
                    // This action reached this step, count it
                    totalAtThisStep++;
                    
                    const state = determineStepState(action, index);
                    
                    switch (state) {
                        case 'completed':
                            completed++;
                            break;
                        case 'running':
                            running++;
                            break;
                        case 'pending':
                            pending++;
                            break;
                        case 'failed': {
                            failed++;
                            const projectId = getProjectIDFromContext(action.spec?.context);
                            if (projectId) {
                                failedProjectNames.push(projectNameById[projectId] || projectId);
                            }
                            break;
                        }
                    }
                }

                const step = workflow?.spec?.action?.steps?.[index];
                let stepType = "Step";
                let stepText = "";

                switch (step?.step?.case) {
                    case "agent":
                        stepType = "Prompt";
                        stepText = step.step.value?.prompt || "";
                        break;
                    case "task":
                        stepType = "Shell script";
                        stepText = step.step.value?.command || "";
                        break;
                    case "pullRequest":
                        stepType = "Pull request";
                        stepText = step.step.value?.title || "";
                        break;
                    case "report":
                        stepType = "Report";
                        stepText = step.step.value?.outputs?.[0]?.title || "";
                        break;
                    default:
                        // stepType and stepText already initialized with defaults
                        break;
                }

                return {
                    stepIndex: index,
                    step: `Step ${index + 1}`,
                    completed,
                    failed,
                    pending,
                    running,
                    excluded, // Actions that failed at earlier steps
                    failedThisStep: failed, // For annotations
                    pendingThisStep: pending, // For annotations
                    runningThisStep: running, // For annotations
                    stepType,
                    stepText,
                    sampleFailedProject: failedProjectNames[0],
                    failedMoreCount: Math.max(failedProjectNames.length - 1, 0),
                    total: totalAtThisStep, // Total step instances at this position (excluding actions that failed earlier)
                    totalActions: actions.length, // Total actions in the workflow
                };
            }),
        [actions, stepsCount, workflow?.spec?.action?.steps, projectNameById, determineStepState],
    );

    // Transform into a sequence with explicit gaps between steps to visually separate bands.
    const data = useMemo(() => {
        const GAP = CHART_DIMENSIONS.gapWidth;
        const rows: Record<string, number | string | null | undefined>[] = [];
        if (stepsCount === 0) return rows;
        // initial gutter at the very start
        rows.push({ x: 0, completed: null, failed: null, pending: null, running: null, excluded: null });
        for (const s of perStep) {
            const leftBoundary = s.stepIndex;
            const start = s.stepIndex + GAP;
            const end = s.stepIndex + 1 - GAP;
            const rightBoundary = s.stepIndex + 1;

            // left gutter (null values) - include stepIndex for hover detection
            rows.push({ x: leftBoundary, completed: null, failed: null, pending: null, running: null, excluded: null, stepIndex: s.stepIndex });
            // start of step (values)
            rows.push({
                x: start,
                step: s.step,
                completed: s.completed,
                failed: s.failed,
                pending: s.pending,
                running: s.running,
                excluded: s.excluded,
                failedThisStep: s.failedThisStep,
                stepIndex: s.stepIndex,
                stepType: s.stepType,
                stepText: s.stepText,
                sampleFailedProject: s.sampleFailedProject,
                failedMoreCount: s.failedMoreCount,
            });
            // center point - this is where the cursor and activeDot will snap to
            rows.push({
                x: s.stepIndex + 0.5,
                step: s.step,
                completed: s.completed,
                failed: s.failed,
                pending: s.pending,
                running: s.running,
                excluded: s.excluded,
                failedThisStep: s.failedThisStep,
                stepIndex: s.stepIndex,
                stepType: s.stepType,
                stepText: s.stepText,
                sampleFailedProject: s.sampleFailedProject,
                failedMoreCount: s.failedMoreCount,
            });
            // keep same value to the end of the step to render a full-width band
            rows.push({
                x: end,
                step: s.step,
                completed: s.completed,
                failed: s.failed,
                pending: s.pending,
                running: s.running,
                excluded: s.excluded,
                failedThisStep: s.failedThisStep,
                stepIndex: s.stepIndex,
                stepType: s.stepType,
                stepText: s.stepText,
                sampleFailedProject: s.sampleFailedProject,
                failedMoreCount: s.failedMoreCount,
            });
            // right gutter (null values) - include stepIndex for hover detection
            rows.push({ x: rightBoundary, completed: null, failed: null, pending: null, running: null, excluded: null, stepIndex: s.stepIndex });
        }
        // final gutter
        rows.push({ x: stepsCount, completed: null, failed: null, pending: null, running: null, excluded: null });
        return rows;
    }, [perStep, stepsCount]);

    const chartConfig = {
        completed: { label: "Completed", color: "rgb(var(--surface-success))" },
        running: { label: "Running", color: "rgb(var(--content-brand))" },
        // use established brand blue tokens
        pending: { label: "Pending", color: "rgb(var(--content-brand))" },
        failed: { label: "Failed", color: "rgb(var(--surface-destructive))" },
    } as const;

    const isEmpty = stepsCount === 0 || actions.length === 0;
    const hasAnyFailed = !isEmpty && perStep.some((s) => (s.failed ?? 0) > 0);
    const hasAnyPending = !isEmpty && perStep.some((s) => (s.pending ?? 0) > 0);
    const hasAnyRunning = !isEmpty && perStep.some((s) => (s.running ?? 0) > 0);

    return (
        <Card variant="bordered" className={cn("bg-surface-primary p-0 shadow-sm", className)}>
            <div className="flex items-center justify-between p-4">
                <Text className="text-lg">Run performance across steps</Text>
                <div className="flex items-center gap-4 pr-1">
                    <div className="flex items-center gap-1.5 text-base">
                        <span className="size-[14px] rounded-[2px] border border-[rgb(var(--surface-success))] bg-[rgb(var(--surface-success)/0.2)]" />
                        <Text className="text-content-primary">Completed</Text>
                    </div>
                    {hasAnyRunning && (
                        <div className="flex items-center gap-1.5 text-base">
                            <span className="size-[14px] rounded-[2px] border border-[rgb(var(--content-brand))] bg-[rgb(var(--content-brand)/0.4)]" />
                            <Text className="text-content-primary">Running</Text>
                        </div>
                    )}
                    {hasAnyPending && (
                        <div className="flex items-center gap-1.5 text-base">
                            <span className="size-[14px] rounded-[2px] border border-[rgb(var(--content-brand))] bg-[rgb(var(--surface-brand))]" />
                            <Text className="text-content-primary">Pending</Text>
                        </div>
                    )}
                    {hasAnyFailed && (
                        <div className="flex items-center gap-1.5 text-base">
                            <span className="size-[14px] rounded-[2px] border border-[rgb(var(--surface-destructive))] bg-[rgb(var(--surface-destructive)/0.2)]" />
                            <Text className="text-content-primary">Failed</Text>
                        </div>
                    )}
                </div>
            </div>
            <div className="py-2">
                {isEmpty ? (
                    <div className="flex h-[260px] items-center justify-center" role="status" aria-live="polite">
                        <Text>No data available</Text>
                    </div>
                ) : (
                    <div
                        role="img"
                        aria-label="Workflow execution performance chart showing completed, pending, and failed actions across steps"
                        tabIndex={0}
                        onKeyDown={handleKeyDown}
                        onMouseMove={handleChartMouseMove}
                        onMouseLeave={handleChartMouseLeave}
                        className="focus:outline-none focus:ring-2 focus:ring-content-brand focus:ring-offset-2 rounded-lg"
                    >
                        <ChartContainer className="h-[260px] w-full" config={chartConfig}>
                            <RechartsPrimitive.AreaChart
                                data={data}
                                // Add small inset so lines/dots don't touch edges
                                margin={{ left: 8, right: 8, top: 8, bottom: 0 }}
                            >
                            <RechartsPrimitive.CartesianGrid
                                vertical={false}
                                strokeDasharray="2 4"
                                stroke="rgb(var(--border-base))"
                                syncWithTicks={true}
                            />
                            {/* Centered step labels: numeric midpoints */}
                            <RechartsPrimitive.XAxis
                                dataKey="x"
                                type="number"
                                domain={[0, Math.max(stepsCount, 1)]}
                                ticks={Array.from({ length: stepsCount }, (_, i) => i + 0.5)}
                                tickFormatter={(v: number) => `Step ${Math.round(v)}`}
                                tickMargin={8}
                                allowDecimals={false}
                            />
                            <RechartsPrimitive.YAxis
                                domain={[0, Math.max(actions.length, 1)]}
                                ticks={[0, Math.max(actions.length, 1) * 0.25, Math.max(actions.length, 1) * 0.5, Math.max(actions.length, 1) * 0.75, Math.max(actions.length, 1)]}
                                tickFormatter={(v: number) => {
                                    const total = Math.max(actions.length, 1);
                                    const pct = (v / total) * 100;
                                    return pct === 0 || pct === 50 || pct === 100 ? `${Math.round(pct)}%` : "";
                                }}
                                width={40}
                                allowDecimals={false}
                            />
                            {/* Draw order: completed (bottom), running (middle-bottom), pending (middle), failed (top) */}
                            {/* Completed: base 0.2, hover 0.45 */}
                            <RechartsPrimitive.Area
                                type="monotone"
                                stackId="1"
                                dataKey="completed"
                                name="completed"
                                stroke={CHART_COLORS.completed.stroke}
                                fill={CHART_COLORS.completed.fill}
                                fillOpacity={CHART_COLORS.completed.fillOpacity}
                                strokeWidth={2}
                                dot={false}
                                activeDot={false}
                                isAnimationActive={false}
                            />

                            {/* Running: base 0.4, hover 0.65 */}
                            {hasAnyRunning && (
                                <RechartsPrimitive.Area
                                    type="monotone"
                                    stackId="1"
                                    dataKey="running"
                                    name="running"
                                    stroke={CHART_COLORS.running.stroke}
                                    fill={CHART_COLORS.running.fill}
                                    fillOpacity={CHART_COLORS.running.fillOpacity}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={false}
                                    isAnimationActive={false}
                                />
                            )}

                            {/* Pending: base 0.35, hover 0.6 */}
                            {hasAnyPending && (
                                <RechartsPrimitive.Area
                                    type="monotone"
                                    stackId="1"
                                    dataKey="pending"
                                    name="pending"
                                    stroke={CHART_COLORS.pending.stroke}
                                    fill={CHART_COLORS.pending.fill}
                                    fillOpacity={CHART_COLORS.pending.fillOpacity}
                                    strokeWidth={0}
                                    dot={false}
                                    activeDot={false}
                                    isAnimationActive={false}
                                />
                            )}

                            {/* Failed */}
                            <RechartsPrimitive.Area
                                type="monotone"
                                stackId="1"
                                dataKey="failed"
                                name="failed"
                                stroke={CHART_COLORS.failed.stroke}
                                fill={CHART_COLORS.failed.fill}
                                fillOpacity={CHART_COLORS.failed.fillOpacity}
                                strokeWidth={0}
                                dot={false}
                                activeDot={false}
                                isAnimationActive={false}
                            />
                            
                            {/* Excluded (ghost space for actions that failed at earlier steps) */}
                            <RechartsPrimitive.Area
                                type="monotone"
                                stackId="1"
                                dataKey="excluded"
                                name="excluded"
                                stroke="transparent"
                                fill="rgb(var(--surface-secondary))"
                                fillOpacity={0.1}
                                strokeWidth={0}
                                dot={false}
                                activeDot={false}
                                isAnimationActive={false}
                            />
                            <ChartTooltip
                                cursor={false}
                                content={<PerformanceTooltip />}
                                allowEscapeViewBox={{ x: false, y: true }}
                            />
                            {/* Background surface-hover at 3% - fills to top of chart */}
                            {hoveredStepIndex !== null && (
                                <RechartsPrimitive.ReferenceArea
                                    x1={hoveredStepIndex}
                                    x2={hoveredStepIndex + 1}
                                    y1={0}
                                    y2={1.03}
                                    fill="rgb(var(--surface-hover))"
                                    fillOpacity={0.03}
                                    ifOverflow="extendDomain"
                                />
                            )}
                            {/* Vertical line at center of hovered step */}
                            {hoveredStepIndex !== null && (
                                <RechartsPrimitive.ReferenceLine
                                    x={hoveredStepIndex + 0.5}
                                    stroke="rgb(var(--border-base))"
                                    strokeWidth={1}
                                    strokeDasharray="3 3"
                                    ifOverflow="extendDomain"
                                />
                            )}
                            {/* Annotations: pending and running spinners only (failures shown in red area) */}
                            {perStep.map((s) =>
                                s.pendingThisStep > 0 && s.total > 0 ? (
                                    <RechartsPrimitive.ReferenceDot
                                        key={`pending-dot-${s.stepIndex}`}
                                        x={s.stepIndex + 0.5}
                                        y={1}
                                        r={0}
                                        fill="none"
                                        stroke="none"
                                        isFront
                                    >
                                        <RechartsPrimitive.Label
                                            content={({ x = 0, y = 0 }) => (
                                                <g transform={`translate(${x}, ${Number(y) - 10})`}>
                                                    <g transform="translate(-10,-2)">
                                                        <IconSpinner
                                                            size="sm"
                                                            className="animate-spin text-[rgb(var(--content-brand))]"
                                                        />
                                                    </g>
                                                    <text
                                                        x={0}
                                                        y={4}
                                                        fontSize={12}
                                                        fill={`rgb(var(--content-brand))`}
                                                        dominantBaseline="middle"
                                                    >
                                                        {s.pendingThisStep}
                                                    </text>
                                                </g>
                                            )}
                                        />
                                    </RechartsPrimitive.ReferenceDot>
                                ) : null,
                            )}
                            {perStep.map((s) =>
                                s.runningThisStep > 0 && s.total > 0 ? (
                                    <RechartsPrimitive.ReferenceDot
                                        key={`running-dot-${s.stepIndex}`}
                                        x={s.stepIndex + 0.5}
                                        y={1}
                                        r={0}
                                        fill="none"
                                        stroke="none"
                                        isFront
                                    >
                                        <RechartsPrimitive.Label
                                            content={({ x = 0, y = 0 }) => (
                                                <g transform={`translate(${x}, ${Number(y) - 10})`}>
                                                    <g transform="translate(-10,-2)">
                                                        <IconSpinner
                                                            size="sm"
                                                            className="animate-spin text-[rgb(var(--content-brand))]"
                                                        />
                                                    </g>
                                                    <text
                                                        x={0}
                                                        y={4}
                                                        fontSize={12}
                                                        fill={`rgb(var(--content-brand))`}
                                                        dominantBaseline="middle"
                                                    >
                                                        {s.runningThisStep}
                                                    </text>
                                                </g>
                                            )}
                                        />
                                    </RechartsPrimitive.ReferenceDot>
                                ) : null,
                            )}
                            </RechartsPrimitive.AreaChart>
                        </ChartContainer>
                    </div>
                )}
            </div>
        </Card>
    );
};

// Custom tooltip that mirrors our popover style and matches the desired content.
type PerformanceTooltipPayload = {
    step: string;
    stepIndex: number;
    stepType: string;
    stepText: string;
    completed: number;
    failed: number;
    pending?: number;
    running?: number;
    failedThisStep: number;
    failedMoreCount: number;
    sampleFailedProject?: string;
};

type PerformanceTooltipProps = {
    active?: boolean;
    payload?: {
        payload: PerformanceTooltipPayload;
    }[];
};

// Truncate text to first sentence or max length
const truncateStepText = (text: string, maxLength = 40): string => {
    if (!text) return "";
    // Get first sentence (up to first period, newline, or maxLength)
    const firstPart = text.split(/[.\n]/)[0] || text;
    const trimmed = firstPart.trim();
    if (trimmed.length <= maxLength) return trimmed;
    return trimmed.slice(0, maxLength - 1) + "…";
};

const PerformanceTooltip: FC<PerformanceTooltipProps> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    // The first payload contains the full data object for the hovered step
    const p = payload[0]?.payload;
    if (!p) return null;

    const truncatedText = truncateStepText(p.stepText);

    return (
        <div className="grid min-w-[16rem] max-w-[24rem] items-start gap-0.5 rounded-lg border border-border-base/50 bg-surface-popover px-3 py-2.5 text-sm text-content-primary shadow-lg backdrop-blur-xl">
            {/* Step number */}
            <div className="text-content-muted">Step {p.stepIndex + 1}</div>
            {/* Step type and description */}
            <div className="mb-1.5">
                <span className="font-medium text-content-primary">{p.stepType}</span>
                {truncatedText && <span className="text-content-strong">: {truncatedText}</span>}
            </div>
            <div className="grid gap-1.5">
                <div className="flex items-center gap-2">
                    <span className="size-[14px] shrink-0 rounded-[2px] border border-[rgb(var(--surface-success))] bg-[rgb(var(--surface-success)/0.2)]" />
                    <Text className="text-content-primary"><span className="font-medium">{p.completed}</span> completed</Text>
                </div>
                {(p.running ?? 0) > 0 && (
                    <div className="flex items-center gap-2">
                        <span className="size-[14px] shrink-0 rounded-[2px] border border-[rgb(var(--content-brand))] bg-[rgb(var(--content-brand)/0.4)]" />
                        <Text className="text-content-primary"><span className="font-medium">{p.running}</span> running</Text>
                    </div>
                )}
                {(p.pending ?? 0) > 0 && (
                    <div className="flex items-center gap-2">
                        <span className="size-[14px] shrink-0 rounded-[2px] border border-[rgb(var(--content-brand))] bg-[rgb(var(--surface-brand))]" />
                        <Text className="text-content-primary"><span className="font-medium">{p.pending}</span> pending</Text>
                    </div>
                )}
                {p.failed > 0 && (
                    <div className="flex items-center gap-2">
                        <span className="size-[14px] shrink-0 rounded-[2px] border border-[rgb(var(--surface-destructive))] bg-[rgb(var(--surface-destructive)/0.2)]" />
                        <Text className="text-content-primary"><span className="font-medium">{p.failed}</span> failed</Text>
                    </div>
                )}
            </div>
        </div>
    );
};
